'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { FaXmark } from 'react-icons/fa6'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import AppStoreLink from '@/app/components/AppStoreLink'
import { track } from '@/utils/analytics'
import { isSensitiveAnalyticsPathname } from '@/utils/analyticsUrl'
import { hasActiveModalDialog } from '@/utils/modalCoordination'
import { CAMPAIGN_EVENTS, type CampaignEvent } from '@/app/components/campaignEvents'

/**
 * EventPopup — one generic, config-driven "save the date" popup that renders
 * whichever campaign in campaignEvents.ts is live right now, then auto-retires
 * it when its window closes. Adding a future event is a data change only.
 *
 * Best practice (researched, July 2026):
 *  - Shows on a short DELAY, never instantly.
 *  - Three ways to close: the X, Escape, and a backdrop click.
 *  - PER-EVENT localStorage key, so dismissing one event never hides another.
 *  - A short GLOBAL cooldown, so two nearby events can't nag back-to-back.
 *  - Calm motion only, fully prefers-reduced-motion aware.
 *  - One bold CTA, no form fields; mobile-safe.
 *  - NEVER on the session's entrance page. Google's intrusive-interstitial
 *    policy demotes mobile pages that cover content right after a visitor
 *    arrives from search, and this site's traffic is mostly articles landed
 *    from Google. So the popup is gated to the 2nd+ pageview of a session:
 *    the page you land on is left clean; once you navigate anywhere, a
 *    dismissible modal is fine and no longer an "entrance interstitial".
 *    The current gate is stricter still: third pageview plus real engagement.
 */

const DISMISS_KEY = (id: string) => `campaign_popup_dismissed_${id}`
const LAST_DISMISS_KEY = 'campaign_popup_last_dismiss' // global cooldown clock
const LAST_SHOWN_KEY = 'campaign_popup_last_shown'
// A 91% dismissal rate means the modal is currently costing more attention
// than it earns. Cap it at one appearance per two weeks even when someone
// follows the CTA (and therefore never explicitly dismisses the event).
const COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000
// Floor delay after the engagement signal fires, so the modal never appears in
// the same frame as the scroll that triggered it.
const SHOW_DELAY_MS = 2000
const MODAL_RECHECK_MS = 500
// Engagement gate (added 2026-08-09). The popup previously opened SHOW_DELAY_MS
// after the 2nd pageview, which meant it interrupted before the reader had read
// anything: 363 views produced 342 dismissals (94%) and fewer than 15 CTA
// clicks in the 28d to 2026-08-09. An offer shown to someone who has read half
// an article is a different proposition from one thrown at someone who just
// arrived, so both of these must now be true before it opens.
const MIN_SESSION_PAGEVIEWS = 3
const ENGAGE_SCROLL_RATIO = 0.6
const ENGAGE_DWELL_MS = 45_000

const FOCUSABLE_ELEMENT_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

function getFocusableElements(container: HTMLElement) {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_ELEMENT_SELECTOR)).filter(
    (element) => {
      const style = window.getComputedStyle(element)
      return (
        element.tabIndex >= 0 &&
        !element.hasAttribute('disabled') &&
        !element.closest('[inert], [aria-hidden="true"]') &&
        style.display !== 'none' &&
        style.visibility !== 'hidden'
      )
    },
  )
}

const parse = (iso: string) => new Date(iso).getTime()

type Remaining = { days: number; hours: number; minutes: number; seconds: number; done: boolean }

function remainingTo(target: number): Remaining {
  const ms = target - Date.now()
  if (ms <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, done: true }
  const s = Math.floor(ms / 1000)
  return {
    days: Math.floor(s / 86400),
    hours: Math.floor((s % 86400) / 3600),
    minutes: Math.floor((s % 3600) / 60),
    seconds: s % 60,
    done: false,
  }
}

/** First event whose window contains `now` and that hasn't been dismissed. */
function pickActiveEvent(now: number): CampaignEvent | null {
  for (const e of CAMPAIGN_EVENTS) {
    if (e.showFrom && now < parse(e.showFrom)) continue
    if (now >= parse(e.hideAfter)) continue
    let dismissed = false
    try {
      dismissed = localStorage.getItem(DISMISS_KEY(e.id)) === '1'
    } catch {
      /* storage blocked — treat as not dismissed */
    }
    if (!dismissed) return e
  }
  return null
}

function withinCooldown(now: number): boolean {
  try {
    const last = Number(
      localStorage.getItem(LAST_SHOWN_KEY) ||
      localStorage.getItem(LAST_DISMISS_KEY) ||
      0,
    )
    return last > 0 && now - last < COOLDOWN_MS
  } catch {
    return false
  }
}

// Session pageview counter for entrance-suppression. sessionStorage survives
// in-tab reloads; an in-memory pair is the fallback when storage is blocked.
// Both dedupe by last path so a StrictMode double-invoke or same-path re-render
// counts a pageview only once. The popup lives in the root layout, so it stays
// mounted across App Router client navigations — hence usePathname drives this.
let memPageviews = 0
let memLastPath: string | null = null
function countPageview(path: string): number {
  try {
    const lastPath = sessionStorage.getItem('mp_pv_path')
    let n = Number(sessionStorage.getItem('mp_pv_count') || '0')
    if (lastPath !== path) {
      n += 1
      sessionStorage.setItem('mp_pv_count', String(n))
      sessionStorage.setItem('mp_pv_path', path)
    }
    return n
  } catch {
    if (memLastPath !== path) {
      memPageviews += 1
      memLastPath = path
    }
    return memPageviews
  }
}

export default function EventPopup() {
  const prefersReducedMotion = useReducedMotion()
  const pathname = usePathname()
  const sensitiveRoute = isSensitiveAnalyticsPathname(pathname)
  const pageType = pathname.startsWith('/blog/')
    ? 'blog'
    : pathname === '/'
      ? 'home'
      : 'static'
  const [eligible, setEligible] = useState(false)
  const [event, setEvent] = useState<CampaignEvent | null>(null)
  const [open, setOpen] = useState(false)
  const [remaining, setRemaining] = useState<Remaining | null>(null)
  const [published, setPublished] = useState(true)
  const modalRootRef = useRef<HTMLDivElement>(null)
  const dialogRef = useRef<HTMLDivElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const dismissRef = useRef<(reason: string) => void>(() => {})

  // Count each pageview of the session (initial load + every client-side
  // navigation). Wait until the third pageview: the entrance page and first
  // navigation stay completely free of campaign interruption.
  // Runs client-only, so the server renders nothing (no hydration mismatch).
  useEffect(() => {
    if (sensitiveRoute) return
    // Deliberate. countPageview mutates a session counter, so it must run as
    // an effect and never during render. Client-only by design.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (countPageview(pathname) >= MIN_SESSION_PAGEVIEWS) setEligible(true)
  }, [pathname, sensitiveRoute])

  useEffect(() => {
    if (!sensitiveRoute) return
    // A capability-bearing unsubscribe route must never inherit a campaign
    // modal or its scroll lock from the previous page.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setOpen(false)
    setEvent(null)
  }, [sensitiveRoute])

  // Once past the entrance page, wait for the reader to actually engage —
  // either scrolling 60% down or staying 45s — and only then open. Gated on
  // `eligible`, which only flips false→true once, so this arms a single time.
  useEffect(() => {
    if (!eligible || sensitiveRoute) return
    const now = Date.now()
    if (withinCooldown(now)) return
    const active = pickActiveEvent(now)
    if (!active) return

    let showTimer: ReturnType<typeof setTimeout> | undefined
    let fired = false

    const open = (trigger: 'scroll' | 'dwell') => {
      if (fired) return
      fired = true
      cleanup()

      const revealWhenUnblocked = () => {
        if (hasActiveModalDialog(document)) {
          showTimer = setTimeout(revealWhenUnblocked, MODAL_RECHECK_MS)
          return
        }

        setEvent(active)
        if (active.eventDate) setRemaining(remainingTo(parse(active.eventDate)))
        setOpen(true)
        try {
          localStorage.setItem(LAST_SHOWN_KEY, String(Date.now()))
        } catch {
          /* storage blocked — event-level dismissal still works this visit */
        }
        // trigger is recorded so we can tell which signal actually earns the
        // CTA clicks, rather than guessing at the next iteration.
        track('campaign_popup_view', {
          event_id: active.id,
          offer_id: active.id,
          trigger,
          cta_surface: `campaign_popup_${active.id}`,
          cta_position: 'modal',
          page_type: pageType,
        })
      }

      showTimer = setTimeout(revealWhenUnblocked, SHOW_DELAY_MS)
    }

    const onScroll = () => {
      const doc = document.documentElement
      const scrollable = doc.scrollHeight - window.innerHeight
      // Pages too short to scroll can never hit the ratio; dwell covers those.
      if (scrollable <= 0) return
      if (window.scrollY / scrollable >= ENGAGE_SCROLL_RATIO) open('scroll')
    }

    const dwellTimer = setTimeout(() => open('dwell'), ENGAGE_DWELL_MS)

    function cleanup() {
      window.removeEventListener('scroll', onScroll)
      clearTimeout(dwellTimer)
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll() // already past the threshold on arrival (restored position)

    return () => {
      cleanup()
      clearTimeout(showTimer)
    }
  }, [eligible, pageType, sensitiveRoute])

  // `published` is time-dependent, so it cannot be derived during render:
  // Next 16's react-hooks/purity correctly flags Date.now() in the render path,
  // because render must be pure and Date.now() returns something different every
  // call. Keyed on `remaining` as well as `event` so it re-evaluates on each
  // countdown tick — the same cadence the old per-render computation had, since
  // that only re-ran when a tick re-rendered the component.
  useEffect(() => {
    // Deliberate. The value depends on wall-clock time, so it must be
    // computed after mount rather than during render or SSR.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPublished(event?.publishAt ? Date.now() >= parse(event.publishAt) : true)
  }, [event, remaining])

  // Live countdown tick while open (only when the event has a future target).
  useEffect(() => {
    if (!open || !event?.eventDate) return
    const target = parse(event.eventDate)
    const id = setInterval(() => setRemaining(remainingTo(target)), 1000)
    return () => clearInterval(id)
  }, [open, event])

  const dismiss = useCallback(
    (reason: string) => {
      setOpen(false)
      if (event) {
        try {
          localStorage.setItem(DISMISS_KEY(event.id), '1')
          localStorage.setItem(LAST_DISMISS_KEY, String(Date.now()))
        } catch {
          /* storage blocked — nothing to persist */
        }
        track('campaign_popup_dismiss', {
          event_id: event.id,
          offer_id: event.id,
          reason,
          cta_surface: `campaign_popup_${event.id}`,
          cta_position: 'modal',
        })
      }
    },
    [event],
  )

  useEffect(() => {
    dismissRef.current = dismiss
  }, [dismiss])

  // Own the full modal lifecycle: isolate the page behind the popup, keep
  // keyboard focus inside it, and return focus to the control that opened it.
  useEffect(() => {
    if (!open || sensitiveRoute) return

    const modalRoot = modalRootRef.current
    const dialog = dialogRef.current
    if (!modalRoot || !dialog) return

    const activeElement = document.activeElement
    const returnFocusTarget = activeElement instanceof HTMLElement ? activeElement : null
    const isolatedElements: Array<{
      element: HTMLElement
      inertValue: string | null
      ariaHiddenValue: string | null
    }> = []

    // EventPopup renders in the root layout rather than a dedicated portal.
    // Isolating every sibling branch up to <body> makes all page content inert
    // while keeping the popup itself interactive.
    let activeBranch: HTMLElement | null = modalRoot
    while (activeBranch && activeBranch !== document.body) {
      const branchParent: HTMLElement | null = activeBranch.parentElement
      if (!branchParent) break

      for (const sibling of Array.from(branchParent.children)) {
        if (sibling === activeBranch || !(sibling instanceof HTMLElement)) continue
        isolatedElements.push({
          element: sibling,
          inertValue: sibling.getAttribute('inert'),
          ariaHiddenValue: sibling.getAttribute('aria-hidden'),
        })
        sibling.setAttribute('inert', '')
        sibling.setAttribute('aria-hidden', 'true')
      }

      activeBranch = branchParent
    }

    const previousBodyOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const focusFrame = window.requestAnimationFrame(() => {
      (closeRef.current ?? getFocusableElements(dialog)[0] ?? dialog).focus({
        preventScroll: true,
      })
    })

    const onKeyDown = (keyboardEvent: KeyboardEvent) => {
      if (keyboardEvent.key === 'Escape') {
        keyboardEvent.preventDefault()
        keyboardEvent.stopPropagation()
        dismissRef.current('escape')
        return
      }

      if (keyboardEvent.key !== 'Tab') return

      const focusableElements = getFocusableElements(dialog)
      if (focusableElements.length === 0) {
        keyboardEvent.preventDefault()
        dialog.focus({ preventScroll: true })
        return
      }

      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]
      const focusedElement = document.activeElement
      const focusIsOutsideDialog =
        focusedElement !== dialog && !dialog.contains(focusedElement)

      if (
        keyboardEvent.shiftKey &&
        (focusedElement === firstElement || focusedElement === dialog || focusIsOutsideDialog)
      ) {
        keyboardEvent.preventDefault()
        lastElement.focus({ preventScroll: true })
      } else if (
        !keyboardEvent.shiftKey &&
        (focusedElement === lastElement || focusedElement === dialog || focusIsOutsideDialog)
      ) {
        keyboardEvent.preventDefault()
        firstElement.focus({ preventScroll: true })
      }
    }

    document.addEventListener('keydown', onKeyDown, true)

    return () => {
      window.cancelAnimationFrame(focusFrame)
      document.removeEventListener('keydown', onKeyDown, true)
      document.body.style.overflow = previousBodyOverflow

      for (const { element, inertValue, ariaHiddenValue } of isolatedElements.reverse()) {
        if (inertValue === null) {
          element.removeAttribute('inert')
        } else {
          element.setAttribute('inert', inertValue)
        }

        if (ariaHiddenValue === null) {
          element.removeAttribute('aria-hidden')
        } else {
          element.setAttribute('aria-hidden', ariaHiddenValue)
        }
      }

      if (returnFocusTarget?.isConnected) {
        returnFocusTarget.focus({ preventScroll: true })
      }
    }
  }, [open, sensitiveRoute])

  const showCountdown = !!event?.eventDate && !remaining?.done
  const isLive = !!event?.eventDate && !!remaining?.done

  // Name + gold accent stay constant across teaser and live (mirrors the App
  // Store card); only the body upgrades to the richer long description once
  // the event is live, and the countdown drops away.
  const headline = event?.headline
  const accent = event?.accent
  const body = isLive && event?.liveBody ? event.liveBody : event?.body

  // The event-card URL (?eventid=) 404s until Apple publishes the event, so
  // during the teaser we fall back to the plain app page (undefined href →
  // AppStoreLink uses the canonical App Store URL). Once published, we use the
  // deep link so the button opens the event card itself.
  const appstoreHref = published && event?.cta.kind === 'appstore' ? event.cta.href : undefined

  const pad = (n: number) => String(n).padStart(2, '0')
  const units = useMemo(
    () =>
      remaining
        ? [
            { label: 'Days', value: remaining.days },
            { label: 'Hours', value: remaining.hours },
            { label: 'Min', value: remaining.minutes },
            { label: 'Sec', value: remaining.seconds },
          ]
        : [],
    [remaining],
  )

  return (
    <AnimatePresence>
      {!sensitiveRoute && open && event && (
        <motion.div
          ref={modalRootRef}
          className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: prefersReducedMotion ? 0 : 0.25 }}
        >
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => dismiss('backdrop')}
            aria-hidden="true"
          />

          <motion.div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="event-popup-title"
            aria-describedby="event-popup-desc"
            tabIndex={-1}
            className="relative w-full max-w-lg overflow-hidden rounded-3xl bg-deepForest shadow-2xl shadow-black/50 ring-1 ring-goldLight/20"
            initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, scale: 0.96, y: 12 }}
            animate={prefersReducedMotion ? { opacity: 1 } : { opacity: 1, scale: 1, y: 0 }}
            exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, scale: 0.97, y: 8 }}
            transition={{ duration: prefersReducedMotion ? 0 : 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -top-24 -right-16 h-56 w-56 rounded-full bg-goldLight/20 blur-3xl"
            />

            <button
              ref={closeRef}
              type="button"
              onClick={() => dismiss('close_button')}
              aria-label="Close"
              className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full text-cream/60 transition-colors hover:bg-white/10 hover:text-cream focus:outline-none focus:ring-2 focus:ring-goldLight/60"
            >
              <FaXmark className="h-4 w-4" aria-hidden="true" />
            </button>

            <div className="relative px-7 pb-8 pt-10 sm:px-10 sm:pb-10 sm:pt-12 text-center">
              <p className="text-[0.7rem] font-semibold uppercase tracking-[0.28em] text-goldLight">
                {event.eyebrow}
              </p>

              <h2
                id="event-popup-title"
                className="mt-4 font-display text-[clamp(1.9rem,6vw,2.75rem)] font-bold leading-[1.08] text-cream"
              >
                {headline}
                {accent && <span className="text-goldLight"> {accent}</span>}
              </h2>

              <p id="event-popup-desc" className="mx-auto mt-4 max-w-sm text-base leading-relaxed text-cream/70">
                {body}
              </p>

              {showCountdown && (
                <div className="mt-7 flex items-stretch justify-center gap-2.5 sm:gap-3">
                  {units.map((u) => (
                    <div
                      key={u.label}
                      className="flex min-w-[3.75rem] flex-col items-center rounded-2xl border border-white/10 bg-white/[0.06] px-2.5 py-3"
                    >
                      <span className="font-display text-2xl font-bold tabular-nums text-cream sm:text-3xl">
                        {pad(u.value)}
                      </span>
                      <span className="mt-1 text-[0.62rem] font-semibold uppercase tracking-[0.15em] text-cream/50">
                        {u.label}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-8 flex flex-col items-center gap-3">
                {event.cta.kind === 'appstore' ? (
                  <AppStoreLink
                    source={`campaign_popup_${event.id}`}
                    href={appstoreHref}
                    ctaPosition="modal"
                    trackingParams={{
                      campaign: event.id,
                      offer_id: event.id,
                      page_type: pageType,
                    }}
                    aria-label="Download on the App Store"
                    className="inline-flex w-full items-center justify-center gap-2.5 rounded-control bg-black px-6 py-3.5 font-semibold text-white shadow-lg shadow-black/40 ring-1 ring-white/10 transition-[color,background-color,border-color,text-decoration-color] duration-200 hover:bg-black/85 sm:w-auto pressable-sm"
                  >
                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.08 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                    </svg>
                    Download on the App Store
                  </AppStoreLink>
                ) : (
                  <Link
                    href={event.cta.href}
                    onClick={() => track('campaign_popup_cta', {
                      event_id: event.id,
                      offer_id: event.id,
                      href: event.cta.kind === 'link' ? event.cta.href : '',
                      cta_surface: `campaign_popup_${event.id}`,
                      cta_position: 'modal',
                    })}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-control bg-goldLight px-6 py-3.5 font-semibold text-deepForest shadow-lg shadow-goldLight/20 transition-[color,background-color,border-color,text-decoration-color] duration-200 hover:bg-goldLight/90 sm:w-auto pressable-sm"
                  >
                    {event.cta.label}
                  </Link>
                )}
                <button
                  type="button"
                  onClick={() => dismiss('maybe_later')}
                  className="text-sm text-cream/50 transition-colors hover:text-cream/80"
                >
                  Maybe later
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
